import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Link2, ArrowRight, Sparkles } from 'lucide-react';
import { useStore } from '@/lib/store';

const MODES = [
  {
    key: 'quick',
    label: 'Quick Cuts',
    meta: '30 sec – 3 min',
    helper: 'Short, punchy clips of the strongest moments.',
  },
  {
    key: 'best',
    label: 'Best Mix',
    meta: 'Mixed lengths',
    helper: 'Strongest moments at whatever length fits — no fixed clip size.',
    badge: 'Featured ✨',
  },
  {
    key: 'deep',
    label: 'Deep Cuts',
    meta: '5 – 7 min',
    helper: 'Longer clips when you need the full context.',
  },
];

const LENGTHS = {
  quick: ['Auto', '30s', '60s', '90s', '2m', '3m'],
  deep:  ['5m', '7m'],
};

export default function UploadPanel() {
  const { state, setState, runExtraction, runRealExtraction } = useStore();
  const [tab, setTab] = useState('file');
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef();

  const acceptExt = ['mp3','wav','m4a','mp4','mov'];
  const validate = (f) => {
    if (!f) return false;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!acceptExt.includes(ext)) {
      toast.error('Unsupported file type', { description: `Accepted: ${acceptExt.map(e => e.toUpperCase()).join(' · ')}` });
      return false;
    }
    if (f.size > 250 * 1024 * 1024) {
      toast.error('File too large', { description: 'Maximum size is 250MB / 2 hours of audio.' });
      return false;
    }
    return true;
  };

  const onFile = (f) => {
    if (!validate(f)) return;
    setFile(f);
    toast.success('File added', { description: `${f.name} · ${(f.size / (1024*1024)).toFixed(1)} MB` });
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    onFile(f);
  };

  const validateUrl = () => {
    if (!/^https?:\/\//i.test(url)) {
      toast.error('Invalid URL', { description: 'Paste a full https:// link to a YouTube video, podcast, or media file.' });
      return false;
    }
    return true;
  };

  const onExtract = () => {
    if (tab === 'file' && !file) {
      toast.error('Add a file first', { description: 'Drop an audio/video file or click Browse.' });
      return;
    }
    if (tab === 'url') {
      if (!url) { toast.error('Paste a link first'); return; }
      if (!validateUrl()) return;
    }
    const modeLabel = MODES.find(m => m.key === state.uploadMode)?.label;
    toast.success('Extracting highlights...', {
      description: `${modeLabel} · ${state.uploadMode !== 'best' ? state.uploadLength : 'smart mix'} · focus: ${state.focus || 'none'}`,
    });

    // Use real backend when file/url is provided, demo for samples
    if (tab === 'file' && file) {
      runRealExtraction({ file });
    } else if (tab === 'url' && url) {
      runRealExtraction({ url });
    } else {
      runExtraction({ sampleId: 'podcast-founder-42' });
    }

    setTimeout(() => {
      document.getElementById('console')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const currentLengths = state.uploadMode === 'quick' ? LENGTHS.quick : state.uploadMode === 'deep' ? LENGTHS.deep : null;

  return (
    <section id="upload" data-testid="upload-section" className="relative py-6 sm:py-10">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="aurora-panel p-6 sm:p-8 reveal" data-testid="upload-panel">
          {/* Tabs */}
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <div className="tab-seg" role="tablist" aria-label="Input type">
              <button
                role="tab"
                aria-selected={tab === 'file'}
                className={`tab-btn ${tab === 'file' ? 'active' : ''}`}
                onClick={() => setTab('file')}
                data-testid="tab-upload-file"
              >
                <Upload className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" /> Upload File
              </button>
              <button
                role="tab"
                aria-selected={tab === 'url'}
                className={`tab-btn ${tab === 'url' ? 'active' : ''}`}
                onClick={() => setTab('url')}
                data-testid="tab-paste-url"
              >
                <Link2 className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" /> Paste URL
              </button>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">MP3 · WAV · M4A · MP4 · MOV · ≤ 2H · ≤ 250MB</span>
          </div>

          {/* Content */}
          {tab === 'file' ? (
            <label
              className={`inner-dark block p-8 sm:p-10 text-center cursor-pointer transition-colors !border !border-white/35 ${dragOver ? 'ring-2 ring-[#20B8F2]/60' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              data-testid="dropzone"
            >
              <input
                ref={fileInput}
                type="file"
                accept=".mp3,.wav,.m4a,.mp4,.mov,audio/*,video/*"
                className="hidden"
                onChange={(e) => onFile(e.target.files?.[0])}
                data-testid="file-input"
              />
              <div className="mx-auto h-10 w-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-3">
                <Upload className="h-4 w-4 text-[#20B8F2]" />
              </div>
              <div className="text-white/90 font-medium">
                {file ? file.name : 'Drop audio or video here'}
              </div>
              <div className="text-white/50 text-sm mt-1">
                {file ? `${(file.size / (1024*1024)).toFixed(1)} MB · click to change` : 'MP3, WAV, M4A, MP4, or MOV — up to 250MB / 2 hours'}
              </div>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); fileInput.current?.click(); }}
                className="btn-ghost mt-4 text-sm"
                data-testid="browse-btn"
              >
                Browse files
              </button>
            </label>
          ) : (
            <div className="inner-dark p-4 !border !border-white/35">
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Paste a YouTube, podcast, or public media link"
                className="w-full bg-transparent border-0 outline-none py-3 px-2 text-white placeholder:text-white/35"
                data-testid="url-input"
              />
            </div>
          )}

          {/* Mode Selector */}
          <div className="mt-6" role="radiogroup" aria-label="Highlight mode">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">Highlight Mode</div>
            <div className="grid sm:grid-cols-3 gap-3">
              {MODES.map(m => (
                <button
                  key={m.key}
                  role="radio"
                  aria-checked={state.uploadMode === m.key}
                  className={`mode-card ${state.uploadMode === m.key ? 'active' : ''}`}
                  onClick={() => setState({ uploadMode: m.key, uploadLength: m.key === 'quick' ? 'auto' : m.key === 'deep' ? '5m' : 'auto' })}
                  data-testid={`mode-${m.key}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-white/95">{m.label}</div>
                    {m.badge && <span className="text-[10px] font-mono uppercase tracking-widest text-[#7EE06D] shrink-0">{m.badge}</span>}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-1.5">{m.meta}</div>
                  <div className="text-white/55 text-xs mt-1.5 leading-relaxed">{m.helper}</div>
                </button>
              ))}
            </div>

            {currentLengths && (
              <div className="flex flex-wrap gap-2 mt-4">
                {currentLengths.map(l => {
                  const val = l.toLowerCase().replace(/\s/g, '');
                  return (
                    <button
                      key={l}
                      onClick={() => setState({ uploadLength: val })}
                      className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest border transition-colors ${
                        state.uploadLength === val
                          ? 'bg-white/10 border-white/25 text-white'
                          : 'bg-transparent border-white/10 text-white/50 hover:text-white'
                      }`}
                      data-testid={`length-${val}`}
                    >
                      {l}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Focus + Cleanup */}
          <div className="mt-6 grid sm:grid-cols-[1fr_auto] gap-4 items-start">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">Focus (optional)</div>
              <div className="inner-dark px-4 py-3 !border !border-white/35">
                <input
                  type="text"
                  value={state.focus}
                  onChange={(e) => setState({ focus: e.target.value })}
                  placeholder="pivot · budget · funny moments · loss aversion · key advice"
                  className="w-full bg-transparent border-0 outline-none text-white text-sm placeholder:text-white/30"
                  data-testid="focus-input"
                />
              </div>
              <button
                type="button"
                onClick={() => setState({ cleanupOn: !state.cleanupOn })}
                className="mt-4 flex items-start gap-3 text-left group"
                data-testid="cleanup-toggle"
                role="switch"
                aria-checked={state.cleanupOn}
              >
                <span
                  className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors ${
                    state.cleanupOn
                      ? 'border-[#7EE06D]/40 bg-[#7EE06D]/80'
                      : 'border-white/10 bg-white/10'
                  }`}
                >
                  <span
                    className={`h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                      state.cleanupOn ? 'translate-x-[18px]' : 'translate-x-[3px]'
                    }`}
                  />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm text-white/85 group-hover:text-white transition-colors">
                    Clean audio before analysis
                  </span>
                  <span className="mt-1 block text-xs leading-relaxed text-white/40">
                    Removes silence, dead air, and filler noise before scanning for highlights.
                  </span>
                </span>
              </button>
            </div>

            <button
              onClick={onExtract}
              className="btn-gradient inline-flex items-center gap-2 self-end sm:self-center"
              data-testid="extract-btn"
            >
              <Sparkles className="h-4 w-4" /> Extract Highlights <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
